/**
 * The four self-service flows, behind one shape.
 *
 * Ory's SDK types `LoginFlow`, `RegistrationFlow`, `RecoveryFlow` and
 * `VerificationFlow` separately, and they differ in fields the renderer never
 * touches. `AnyFlow` is the part `FlowForm` actually needs — an id and a `ui` —
 * so one component serves all four without a union that has to be narrowed at
 * every use.
 */

import type { StepUp } from '@/auth/flow'
import type {
  FrontendApi,
  LoginFlow,
  RecoveryFlow,
  RegistrationFlow,
  UiContainer,
  SettingsFlow,
  VerificationFlow,
} from '@ory/client-fetch'

export interface AnyFlow {
  readonly id: string
  readonly ui: UiContainer
}

export type FlowKind = 'login' | 'registration' | 'recovery' | 'verification' | 'settings'

/**
 * The flows reachable without a session.
 *
 * `settings` is deliberately excluded: it requires a session and lives behind
 * the gate, so putting it on the unauthenticated page would be a route anyone
 * could reach and Ory would refuse with a 401 nobody could act on. The compiler
 * enforces the split rather than a comment asking people to remember it.
 */
export type AuthFlowKind = Exclude<FlowKind, 'settings'>

/**
 * Create and update a flow of a given kind.
 *
 * A small dispatch table rather than four near-identical page components. The
 * `updateXFlowBody` types are unions of every method the tenant might have
 * enabled, and the values we send come from a `FormData` walk over whatever
 * nodes Ory rendered — so the cast is at the boundary where the type system
 * genuinely cannot help, and nowhere else.
 */
export interface FlowDriver {
  create: () => Promise<AnyFlow>
  update: (flowId: string, body: Record<string, unknown>) => Promise<unknown>
  /**
   * Fetch a flow that already exists, by id.
   *
   * Separate from `create` because for a settings flow the two are NOT
   * interchangeable, and the difference is a broken enrolment (#1025).
   * A settings flow carries the TOTP secret it minted, so creating a second one
   * mints a SECOND secret and discards the first — leaving whatever the
   * customer already scanned pointing at a secret Ory no longer holds.
   *
   * That happens on an ordinary path: Ory asks for a fresh session part-way
   * through enrolment, the page sends the customer to sign in, and the return
   * journey has to resume THAT flow rather than start another.
   *
   * Absent on the drivers where resuming is meaningless, so a caller has to
   * check rather than receive a silent no-op.
   */
  readonly resume?: (flowId: string) => Promise<AnyFlow>
}

/**
 * How a login flow is created, when it is not an ordinary sign-in (#47).
 *
 * Only `login` reads this, and only when something sent the customer here to
 * re-authenticate. Ory needs to be told which, because the two refusals want
 * different things: `refresh` re-asks for the same factor, `aal2` asks for a
 * second one. Neither substitutes for the other.
 */
export interface LoginIntent {
  readonly stepUp?: StepUp
}

export function flowDriver(ory: FrontendApi, kind: FlowKind, intent: LoginIntent = {}): FlowDriver {
  switch (kind) {
    case 'login':
      return {
        // `refresh: false` for an ordinary visit — arriving at the page is not
        // a step-up, and asking for a refresh would demand credentials from
        // someone merely looking.
        //
        // But a step-up is EXACTLY when it must not be false. A request that
        // already carries a valid session and does not say `refresh=true` is
        // answered `400 session_already_available`, so the hardcoded `false`
        // made the step-up path unreachable even once the navigation to it was
        // fixed (#47). And `aal2` is a different parameter, not a stronger
        // refresh: `refresh=true` re-asks for the first factor, which is
        // precisely what a customer refused for want of a SECOND factor has
        // already provided.
        create: () =>
          ory.createBrowserLoginFlow(
            intent.stepUp === 'aal2' ? { aal: 'aal2' } : { refresh: intent.stepUp === 'refresh' },
          ) as Promise<LoginFlow>,
        update: (flow, body) =>
          ory.updateLoginFlow({ flow, updateLoginFlowBody: body as never }),
      }
    case 'registration':
      return {
        create: () => ory.createBrowserRegistrationFlow({}) as Promise<RegistrationFlow>,
        update: (flow, body) =>
          ory.updateRegistrationFlow({ flow, updateRegistrationFlowBody: body as never }),
      }
    case 'recovery':
      return {
        create: () => ory.createBrowserRecoveryFlow() as Promise<RecoveryFlow>,
        update: (flow, body) =>
          ory.updateRecoveryFlow({ flow, updateRecoveryFlowBody: body as never }),
      }
    case 'verification':
      return {
        create: () => ory.createBrowserVerificationFlow({}) as Promise<VerificationFlow>,
        update: (flow, body) =>
          ory.updateVerificationFlow({ flow, updateVerificationFlowBody: body as never }),
      }
    case 'settings':
      // The only flow that requires a session.
      //
      // It is NOT the only one whose success is a 200 carrying the updated
      // flow, which this comment used to claim. `updateRecoveryFlow` and
      // `updateVerificationFlow` are typed `Promise<RecoveryFlow>` and
      // `Promise<VerificationFlow>` and do exactly that at
      // `choose_method -> sent_email`. Believing otherwise is why `AuthPage`
      // discarded what they returned, and why "check your email" never
      // appeared (#29).
      return {
        create: () => ory.createBrowserSettingsFlow({}) as Promise<SettingsFlow>,
        // The SAME flow, and with it the same TOTP secret. See `resume`.
        resume: (id) => ory.getSettingsFlow({ id }) as Promise<SettingsFlow>,
        update: (flow, body) =>
          ory.updateSettingsFlow({ flow, updateSettingsFlowBody: body as never }),
      }
  }
}
